import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { subscribeTable } from '../lib/realtime';
import type { DashboardStats } from '../lib/types';

interface UseDashboardStatsResult {
  stats: DashboardStats | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

const EMPTY_STATS: DashboardStats = {
  activeDrivers: 0,
  offlineDrivers: 0,
  newDriversThisMonth: 0,
  bookedTrips: 0,
  cancelledTrips: 0,
  totalEarningThisMonth: 0,
  todayIncome: 0,
  weekIncome: 0,
  monthIncome: 0,
};

export function useDashboardStats(companyId?: string | null): UseDashboardStatsResult {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const now = new Date();
      const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
      const startOfWeek = new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay()).toISOString();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();

      const driverQ = (statusFilter?: { eq?: string; in?: string[] }) => {
        let q = supabase.from('drivers').select('*', { count: 'exact', head: true });
        if (companyId) q = q.eq('company_id', companyId);
        if (statusFilter?.eq) q = q.eq('status', statusFilter.eq);
        if (statusFilter?.in) q = q.in('status', statusFilter.in);
        return q;
      };
      const tripFareQ = (fromIso: string) => {
        let q = supabase.from('trips').select('fare').eq('status', 'completed').gte('ended_at', fromIso);
        if (companyId) q = q.eq('company_id', companyId);
        return q;
      };
      const [
        { count: activeDrivers },
        { count: offlineDrivers },
        { count: newDrivers },
        { count: bookedTrips },
        { count: cancelledTrips },
        { data: monthTrips },
        { data: todayTrips },
        { data: weekTrips },
      ] = await Promise.all([
        driverQ({ eq: 'active' }),
        driverQ({ in: ['inactive', 'suspended'] }),
        (() => {
          let q = supabase.from('drivers').select('*', { count: 'exact', head: true }).gte('created_at', startOfMonth);
          if (companyId) q = q.eq('company_id', companyId);
          return q;
        })(),
        (() => {
          let q = supabase.from('trips').select('*', { count: 'exact', head: true }).eq('status', 'completed');
          if (companyId) q = q.eq('company_id', companyId);
          return q;
        })(),
        (() => {
          let q = supabase.from('trips').select('*', { count: 'exact', head: true }).eq('status', 'cancelled');
          if (companyId) q = q.eq('company_id', companyId);
          return q;
        })(),
        tripFareQ(startOfMonth),
        tripFareQ(startOfToday),
        tripFareQ(startOfWeek),
      ]);

      const sum = (rows: { fare: number | null }[] | null) =>
        (rows ?? []).reduce((acc, r) => acc + (r.fare ?? 0), 0);

      setStats({
        activeDrivers: activeDrivers ?? 0,
        offlineDrivers: offlineDrivers ?? 0,
        newDriversThisMonth: newDrivers ?? 0,
        bookedTrips: bookedTrips ?? 0,
        cancelledTrips: cancelledTrips ?? 0,
        totalEarningThisMonth: sum(monthTrips),
        todayIncome: sum(todayTrips),
        weekIncome: sum(weekTrips),
        monthIncome: sum(monthTrips),
      });
    } catch {
      setError('Failed to load dashboard stats');
      setStats(EMPTY_STATS);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    fetchStats();
    const unsubTrips = subscribeTable('trips', fetchStats);
    const unsubSessions = subscribeTable('driving_sessions', fetchStats);
    const unsubTx = subscribeTable('transactions', fetchStats);
    const unsubDrivers = subscribeTable('drivers', fetchStats);
    return () => {
      unsubTrips();
      unsubSessions();
      unsubTx();
      unsubDrivers();
    };
  }, [fetchStats]);

  return { stats, loading, error, refetch: fetchStats };
}
